import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { SVGFC } from '@repo/toolkit/types'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import Badge from '../../atoms/badge'
import Typography from '../../atoms/typography'

export type Tab<
  T extends 'required-key' | 'optional-key' = 'optional-key',
  U extends string = string
> = {
  label: string
  /** display a badge number on the tab */
  badgeNumber?: string
  /** display an icon on the tab */
  /** disable the tab, no click and no background animation */
  disabled?: boolean
  component?: React.ReactNode
  outline?: boolean
  isCollapsed?: boolean
  icon?: SVGFC
} & (T extends 'required-key' ? { key: U } : { key?: U })

export interface TabsProps {
  className?: string
  variant?: 'primary' | 'secondary' | 'tertiary'
  tabs: Array<Tab>
  activeTab: number
  customTabComponent?: React.ReactNode // This goes at the end of the tabs, it's more likely used for action buttons
  outline?: boolean
  size?: 'full' | 'small' // Add size prop
  iconPosition?: 'left' | 'right'
  hideDisabled?: boolean
  /** let long labels wrap on several lines so every tab stays visible */
  wrapLabels?: boolean
  onTabChange?: (tabIndex: number, label: string) => void
}

const CapsuleTab: React.FC<TabsProps> = ({
  className,
  tabs,
  activeTab,
  variant = 'primary',
  customTabComponent,
  outline,
  size = 'full', // Default size is 'full'
  iconPosition = 'right',
  onTabChange,
  hideDisabled = false, // Default to false
  wrapLabels = false
}) => {
  const [backgroundStyle, setBackgroundStyle] = useState({ left: 0, width: 0 })
  const tabsRef = useRef<HTMLButtonElement[]>([])

  // Filter out disabled tabs if hideDisabled is true
  const visibleTabs = useMemo(
    () => (hideDisabled ? tabs.filter((tab) => !tab.disabled) : tabs),
    [hideDisabled, tabs]
  )
  const visibleActiveTab = useMemo(
    () =>
      hideDisabled ? visibleTabs.findIndex((tab) => tabs.indexOf(tab) === activeTab) : activeTab,
    [hideDisabled, visibleTabs, tabs, activeTab]
  )

  const updateBackgroundStyle = () => {
    const activeTabElement = tabsRef.current[visibleActiveTab]
    if (activeTabElement) {
      const { offsetLeft, offsetWidth } = activeTabElement
      setBackgroundStyle({ left: offsetLeft, width: offsetWidth })
    }
  }

  useEffect(() => {
    // Initial update of background style

    updateBackgroundStyle()

    // Update background style on window resize
    window.addEventListener('resize', updateBackgroundStyle)

    return () => {
      window.removeEventListener('resize', updateBackgroundStyle)
    }
  }, [visibleActiveTab, visibleTabs])

  const textColor: Record<typeof variant, string> = {
    primary: tailwindColors.white.DEFAULT,
    secondary: tailwindColors.secondary.DEFAULT,
    tertiary: tailwindColors.primary.DEFAULT
  }

  const iconColor = (tab: Tab<'optional-key', string>, index: number) =>
    tab.disabled
      ? tailwindColors.grey.light.hover
      : index === visibleActiveTab
        ? textColor[variant]
        : tailwindColors.grey.lighter

  return (
    <>
      <div
        className={cn(
          'flex w-full justify-between relative p-3 rounded-3xl',
          {
            shadow: !outline,
            'bg-grey-light': variant === 'primary',
            'bg-white': variant === 'secondary',
            'border border-grey-light rounded-xl': outline,
            'p-3': size === 'full',
            'p-1 rounded-[12px]': size === 'small'
          },
          className
        )}
      >
        <div
          className={cn('overflow-auto scrollbar-hide ', {
            'w-full': !customTabComponent
          })}
        >
          <div className="relative flex justify-between w-full">
            <div
              className={cn('absolute bottom-0 h-full rounded-xl transition-all duration-300', {
                'bg-secondary': variant === 'primary',
                'bg-secondary-light': variant === 'secondary',
                'bg-primary-light': variant === 'tertiary',
                'rounded-sm': size === 'small'
              })}
              style={{ left: backgroundStyle.left, width: backgroundStyle.width }}
            />
            {visibleTabs.map((tab, index) => (
              <button
                type="button"
                key={index}
                ref={(el) => (tabsRef.current[index] = el as HTMLButtonElement)}
                className={cn('cursor-pointer relative flex items-center justify-center gap-2', {
                  'whitespace-nowrap': !wrapLabels,
                  'text-center': wrapLabels,
                  'py-2.5 px-5': size === 'full',
                  'py-2 px-3': size === 'small',
                  'mr-2.5': index !== visibleTabs.length - 1 || customTabComponent,
                  'flex-1': variant === 'secondary' && !wrapLabels,
                  'flex-auto': variant === 'secondary' && wrapLabels
                })}
                disabled={tab.disabled}
                onClick={() => {
                  if (tab.disabled) return
                  if (onTabChange) onTabChange(tabs.indexOf(tab), tab.label)
                }}
              >
                {tab.icon && iconPosition === 'left' && (
                  <tab.icon
                    color={iconColor(tab, index)}
                    className={cn({
                      'w-6 h-6': size === 'full', // Full size icon
                      'w-4 h-4': size === 'small' // Small size icon
                    })}
                  />
                )}

                <Typography
                  className="transition-colors"
                  variant={size === 'small' ? 'label-semibold' : 'secondary-body'}
                  color={
                    tab.disabled
                      ? tailwindColors.grey.light.hover
                      : index === visibleActiveTab
                        ? textColor[variant]
                        : tailwindColors.grey.lighter
                  }
                >
                  {tab.label}
                </Typography>

                {tab.icon && iconPosition === 'right' && (
                  <tab.icon
                    color={iconColor(tab, index)}
                    className={cn({
                      'w-6 h-6': size === 'full', // Full size icon
                      'w-4 h-4': size === 'small' // Small size icon
                    })}
                  />
                )}

                {tab.badgeNumber && (
                  <Badge
                    variant={index === visibleActiveTab ? 'warning' : 'grey'}
                    size={size === 'full' ? 'small' : 'xsmall'}
                    text={tab.badgeNumber}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
        {customTabComponent}
      </div>
      {tabs[visibleActiveTab]?.component && <div>{tabs[visibleActiveTab].component}</div>}
    </>
  )
}

export default CapsuleTab
